import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';

import { Logins } from '../../helpers/getPossibleLogins';
import { NotYou } from '../NotYou/NotYou';
import { HandleIdpSsoLoginArgs } from '../../types/ssoLogin';

import './PossibleLogins.scss';

export interface SelectedLogin {
  regionEnv: OP.Env;
  heliumEndpoint: string;
  namespace: Logins['namespaces'][0];
}

interface Namespaces {
  email: string;
  logins: Logins[];
  isPasswordReset?: boolean;
  resetLogins: () => void;
  setSelectedLogin(value: SelectedLogin): void;
  handleIdpSsoLogin(args: HandleIdpSsoLoginArgs): void;
}

/**
 * @component
 * @description Component that allows the user to choose a namespace to login with.
 * @param {string} props.email - email they are using to login with.
 * @param {Logins[]} props.login - the regions and namespaces the user has to choose from
 * @param {function} props.restLogins - callback to reset the possible logins and the user's email takes you back to emailForm
 * @param {function} props.setSelectedLogin - c callback to set the chosen namespace
 */
export const PossibleLogins = ({
  email,
  logins,
  isPasswordReset,
  resetLogins,
  setSelectedLogin,
  handleIdpSsoLogin,
}: Namespaces) => {
  const { t } = useTranslation();
  const selectDelimiter = ':';

  const loginHandler = (
    regionName?: string | null,
    namespaceId?: string | number | null,
  ) => {
    const region = logins.find(
      (loginRegion) => loginRegion.name === regionName,
    );
    const namespace = region?.namespaces.find(
      (ns) => ns.id === Number(namespaceId),
    );

    if (region && namespace) {
      // Check if the namespace is of type 'identityProvider' and SSO is enabled.
      // If true, initiate the SSO login process. Otherwise, set the selected login options
      if (
        namespace.namespaceType?.modelName === 'identityProvider' &&
        namespace.identityProvider?.isSsoEnabled
      ) {
        handleIdpSsoLogin({
          email,
          namespaceNickname: namespace.nickname!,
          env: region.env,
        });
      } else {
        setSelectedLogin({
          regionEnv: region.env!,
          heliumEndpoint: region.helium?.endpoint!,
          namespace,
        });
      }
    }
  };

  const handleLoginClick: ComponentProps<typeof OpButton>['onClick'] = async (
    e,
  ) => {
    const regionName = e.currentTarget.getAttribute('data-region');
    const namespaceId = e.currentTarget.getAttribute('data-namespace');

    loginHandler(regionName, namespaceId);
  };

  const handleSelectOptionClick = async (value: string) => {
    const [regionName, namespaceId] = value.split(selectDelimiter);

    loginHandler(regionName, namespaceId);
  };

  const hasAlotOfNameSpaces =
    logins.reduce((memo, login) => {
      return memo + login.namespaces.length;
    }, 0) > 10;

  const renderDropdown = () => {
    const selectOptions = logins.map((login) => {
      return {
        label: login.name,
        options: login.namespaces.map((namespace) => ({
          label: generateButtonText(namespace),
          value: `${login.name}${selectDelimiter}${namespace.id}`,
        })),
      };
    });

    return (
      <div className="possible-logins__select">
        <OpSelect
          options={selectOptions}
          placeholder={t('Select your namespace')}
          showSearch
          onChange={handleSelectOptionClick}
        />
      </div>
    );
  };

  const generateButtonText = (namespace: Logins['namespaces'][0]) => {
    const { org, identityProvider } = namespace;
    // If org is at the top level it is not an IdP namespace
    if (org) {
      return org.name;
    }

    // These should all exist but leaning on inference
    if (identityProvider && identityProvider.org) {
      return identityProvider.isSsoEnabled ? (
        <>SSO: {identityProvider.org.name}</>
      ) : (
        identityProvider.org.name
      );
    }

    // Probably master mode account
    return namespace.nickname;
  };

  const renderDefault = () => (
    <ul className="possible-logins__region-list">
      {logins.map((loginRegion) => {
        return (
          <li key={loginRegion.env} className="possible-logins__region">
            <p className="possible-logins__region-name">{loginRegion.name}</p>
            <ul className="possible-logins__namespace-list">
              {loginRegion.namespaces.map((namespace) => (
                <li key={namespace.id} className="possible-logins__namespace">
                  <OpButton
                    onClick={handleLoginClick}
                    data-region={loginRegion.name}
                    data-namespace={namespace.id}
                    className="possible-logins__button"
                  >
                    {generateButtonText(namespace)}
                  </OpButton>
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div data-testid="possible-logins" className="possible-logins">
      <NotYou
        header={
          isPasswordReset
            ? t('Choose namespace to reset password')
            : t('Where would you like to sign in?')
        }
        emailPrefix=""
        email={email}
        onResetClick={resetLogins}
      />
      {hasAlotOfNameSpaces ? renderDropdown() : renderDefault()}
    </div>
  );
};
